// Helpers for submitting a batch of callable-function requests without
// stampeding a cold function, and for absorbing the transient failures that
// come with one. Kept free of React and Firebase imports so the behaviour is
// directly testable.

// How many certificate requests are in flight at once. A job can hold dozens
// of items; firing them all simultaneously means dozens of concurrent cold
// starts, which is what produced sporadic INTERNAL/500s on the first run after
// a deploy.
export const DEFAULT_CONCURRENCY = 4;

// Callable error codes worth retrying: the request never got a verdict, for
// reasons unrelated to whether it was valid. Codes that describe the request
// itself — failed-precondition (the item genuinely isn't certifiable),
// unauthenticated, invalid-argument, not-found, permission-denied — are
// deliberately absent: retrying those just repeats the same answer.
const RETRYABLE_CODES = new Set([
  "internal",
  "unavailable",
  "deadline-exceeded",
  "resource-exhausted",
  "aborted",
  "cancelled",
  "unknown",
]);

export function isRetryable(error) {
  if (!error) return false;
  // The modular SDK reports codes as "functions/internal".
  const code = String(error.code || "").replace(/^functions\//, "");
  return RETRYABLE_CODES.has(code);
}

const defaultSleep = (ms) =>
  new Promise((resolve) => setTimeout(resolve, ms));

// Run `fn`, retrying only transient failures. Returns fn's value, or rethrows
// the last error once the attempts are spent.
export async function withRetry(
  fn,
  {
    retries = 1,
    delayMs = 800,
    shouldRetry = isRetryable,
    onRetry,
    sleep = defaultSleep,
  } = {}
) {
  let attempt = 0;
  for (;;) {
    try {
      return await fn(attempt);
    } catch (error) {
      if (attempt >= retries || !shouldRetry(error)) throw error;
      attempt += 1;
      if (onRetry) onRetry(error, attempt);
      // Back off a little so a retry doesn't land in the same cold-start crush.
      await sleep(delayMs * attempt);
    }
  }
}

// Map over `items` with at most `limit` calls in flight, preserving input
// order in the results. `fn` is expected to settle rather than throw; an
// exception propagates and abandons the remaining items.
export async function mapWithConcurrency(items, limit, fn) {
  const list = Array.from(items || []);
  const results = new Array(list.length);
  let cursor = 0;

  const worker = async () => {
    for (;;) {
      const index = cursor;
      cursor += 1;
      if (index >= list.length) return;
      results[index] = await fn(list[index], index);
    }
  };

  const workers = Math.max(1, Math.min(limit, list.length));
  if (list.length === 0) return results;
  await Promise.all(Array.from({ length: workers }, worker));
  return results;
}
